import {ServerInMemoryCache} from '@aboutyou/storage';
import {CHECKOUT_WC_JS_PATH} from './checkout-constants';
import {CheckoutPreloadData} from './types';

const FIVE_MINUTES = 5 * 60 * 1000;
const FETCH_TIMEOUT_MS = 800;

const checkoutPreloadCache = new ServerInMemoryCache<CheckoutPreloadData>({
  ttl: FIVE_MINUTES,
});

export const getCheckoutPreloadData = async (
  shopId: number | string,
  checkoutFrontendHost: string,
): Promise<CheckoutPreloadData> => {
  const CHECKOUT_PRELOADER_SCRIPT_URL = `${checkoutFrontendHost}/${CHECKOUT_WC_JS_PATH}?appId=${shopId}`;

  const cached = checkoutPreloadCache.get(CHECKOUT_PRELOADER_SCRIPT_URL);
  if (cached) {
    return Promise.resolve(cached);
  }

  const result = await fetchWithTimeout(
    CHECKOUT_PRELOADER_SCRIPT_URL,
    FETCH_TIMEOUT_MS,
  )
    .then(response => response.text())
    .then(response => {
      const textData = JSON.stringify(response);
      const lines = textData.split(';');
      for (const line of lines) {
        if (line.includes('script.src')) {
          // get encoded string from src
          const base64String = line.split('"')[1];
          // if string assigned to src exists we can assume those will have necessary versions and hashes
          if (base64String) {
            const convertedUrl = Buffer.from(base64String, 'base64').toString();

            const {version, hash} = extractVersionAndHash(convertedUrl);

            if (!version || !hash) {
              throw new Error('Error parsing checkout script');
            }
            return {version, hash, content: response};
          }
        }
      }
      throw new Error('Error parsing checkout script');
    });

  checkoutPreloadCache.set(CHECKOUT_PRELOADER_SCRIPT_URL, result);

  return result;
};

export const extractVersionAndHash = (text: string) => {
  // Find all segments that start with /v in the URL
  const segments = text.split('/').filter(segment => segment.startsWith('v'));

  if (segments.length < 2) {
    return {version: undefined, hash: undefined};
  }

  // First segment with /v is the version
  const version = segments[0];
  // Second segment with /v is the hash
  const hash = segments[1];

  return {version, hash};
};

const fetchWithTimeout = async (url: string, timeoutMs: number) => {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeoutPromise = new Promise<Response>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error(`Fetch to ${url} timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    const response = await Promise.race([fetch(url), timeoutPromise]);
    return response as Response;
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
};
